import React, {useEffect, useImperativeHandle, useRef} from 'react'
import Code from '@tiptap/extension-code'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder, {PlaceholderOptions} from '@tiptap/extension-placeholder'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import {Editor, EditorContent, EditorEvents, Extension, Extensions, FocusPosition, HTMLContent, useEditor} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import './rich-input.scss'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'

export interface RichInputRef {
  focus(position?: FocusPosition): void
  blur(): void
  quote(content: HTMLContent): void
  isFocused: boolean
  editor: Editor | null
}

export interface RichInputProps {
  value?: HTMLContent | null
  readOnly?: boolean
  placeholder?: PlaceholderOptions['placeholder']
  className?: string
  style?: React.CSSProperties
  blurOnEscape?: boolean
  extensions?: Extensions
  onMetaEnter?: () => void
  onEditorBlur?: (value: HTMLContent) => void
  onChange?: (value: HTMLContent) => void
  onFocus?: (e: EditorEvents['focus']) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLDivElement>, editor: Editor | null) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>, editor: Editor | null) => void
}

const RichInput = React.forwardRef<RichInputRef, RichInputProps>(
  (
    {
      value,
      onEditorBlur,
      onChange,
      placeholder = '...',
      onKeyUp,
      onKeyDown,
      onFocus,
      onMetaEnter,
      style,
      readOnly = false,
      className,
      blurOnEscape = true,
      extensions = [],
    },
    ref
  ) => {
    const internalContentRef = useRef<string>(value || '')
    const isUpdatingRef = useRef<boolean>(false)

    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            code: false,
            codeBlock: false,
          }),
          Placeholder.configure({
            placeholder,
          }),
          Link,
          TaskList,
          TaskItem,
          Underline,
          Code,
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
          Image,
          TextStyle,
          Color,
          ...extensions,
          Extension.create({
            addKeyboardShortcuts() {
              return {
                'Meta-Enter': () => Boolean(onMetaEnter != null),
              }
            },
          }),
        ],
        editable: !readOnly,
        content: value,
        onBlur: (e) => {
          const value = e.editor.getHTML()
          setTimeout(() => {
            if (!e.editor.isFocused) onEditorBlur?.(value)
          }, 200)
        },
        onUpdate: (e) => {
          const newContent = e.editor.getHTML()
          internalContentRef.current = newContent
          onChange?.(newContent)
        },
        onFocus: (e) => onFocus?.(e),
      },
      []
    )

    useEffect(() => {
      if (!editor || isUpdatingRef.current) return
      if (value === internalContentRef.current) return

      const normalizedValue = value?.replace(/\n/g, '<br />') || ''
      const normalizedInternal = internalContentRef.current?.replace(/\n/g, '<br />') || ''
      if (normalizedValue === normalizedInternal) return

      isUpdatingRef.current = true
      try {
        const commandsChain = editor.chain()
        if (value) {
          commandsChain.setContent(value.replace(/\n/g, '<br />'), false)
        } else {
          commandsChain.clearContent(false)
        }
        commandsChain.run()
        internalContentRef.current = editor.getHTML()
      } finally {
        isUpdatingRef.current = false
      }
    }, [editor, value])

    useEffect(() => {
      if (!editor) return

      editor.setOptions({
        editable: !readOnly,
      })
    }, [editor, readOnly])

    useImperativeHandle(
      ref,
      () => ({
        isFocused: editor?.isFocused ?? false,
        editor,
        blur: () => editor?.chain().blur().run(),
        focus: (position) =>
          editor
            ?.chain()
            .focus(position ?? 'end')
            .run(),
        quote: (content) => {
          editor?.chain().setBlockquote().insertContent(content).insertContent('<p></p>').unsetBlockquote().focus().run()
        },
      }),
      [editor]
    )

    return (
      <>
        <EditorContent
          editor={editor}
          className={className}
          style={style}
          onKeyUp={(e) => {
            onKeyUp?.(e, editor)
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e, editor)
            if (e.key === 'Enter' && e.metaKey) onMetaEnter?.()
            if (e.key === 'Escape' && blurOnEscape) editor?.commands.blur()
          }}
        />
      </>
    )
  }
)

export default RichInput
